import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, CheckCircle, AlertCircle, Loader2, Send, MessageCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationSettings({ businessName }) {
  const { permission, isSupported, requestPermission, sendNotification, notifyNewLead } = usePushNotifications();
  const [requesting, setRequesting] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);

  // Check WhatsApp configuration status
  useEffect(() => {
    const checkWhatsappStatus = async () => {
      try {
        const response = await axios.get(`${API}/notifications/whatsapp/status`);
        setWhatsappStatus(response.data);
      } catch (error) {
        console.error('Error checking WhatsApp status:', error);
        setWhatsappStatus({ configured: false });
      }
    };
    checkWhatsappStatus();
  }, []);

  const handleEnableNotifications = async () => {
    setRequesting(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        toast.success('Notificaciones activadas correctamente');
        sendNotification('Notificaciones Activadas', {
          body: 'Recibirás alertas cuando lleguen nuevos leads',
          tag: 'setup-complete'
        });
      } else {
        toast.error('Permiso de notificaciones denegado');
      }
    } catch (error) {
      toast.error('Error al activar notificaciones');
    } finally {
      setRequesting(false);
    }
  };

  const handleTestNotification = () => {
    notifyNewLead({
      senderName: 'Usuario de Prueba',
      platform: 'facebook_messenger',
      message: '¡Hola! Me interesa cotizar un vehículo. ¿Tienen disponibilidad?',
      businessName: businessName
    });
    toast.success('Notificación de prueba enviada');
  };

  const handleTestWhatsapp = async () => {
    setTestingWhatsapp(true);
    try {
      await axios.post(`${API}/notifications/whatsapp/test`);
      toast.success('Notificación de WhatsApp enviada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar notificación de WhatsApp');
    } finally {
      setTestingWhatsapp(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          <div>
            <h4 className="text-yellow-400 font-medium">Navegador no compatible</h4>
            <p className="text-yellow-300/70 text-sm mt-1">
              Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox, Edge o Safari.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Browser Notifications */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${permission === 'granted' ? 'bg-green-500/20' : 'bg-slate-700'}`}>
            {permission === 'granted' ? (
              <Bell className="w-5 h-5 text-green-400" />
            ) : (
              <BellOff className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <div>
            <h4 className="text-white font-medium">Notificaciones del Navegador</h4>
            <p className="text-slate-400 text-sm">
              {permission === 'granted' 
                ? 'Recibirás alertas cuando lleguen nuevos leads'
                : 'Activa las notificaciones para no perder ningún lead'}
            </p>
          </div>
        </div>

        {permission === 'granted' ? (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Activadas
            </span>
            <button
              onClick={handleTestNotification}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors text-sm"
            >
              <Send className="w-4 h-4" />
              Probar
            </button>
          </div>
        ) : permission === 'denied' ? (
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Bloqueadas</span>
          </div>
        ) : (
          <button
            onClick={handleEnableNotifications}
            disabled={requesting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50"
          >
            {requesting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Bell className="w-4 h-4" />
            )}
            Activar
          </button>
        )}
      </div>

      {permission === 'denied' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-red-300 text-sm">
            Las notificaciones están bloqueadas. Para activarlas:
          </p>
          <ol className="text-red-300/70 text-sm mt-2 list-decimal list-inside space-y-1">
            <li>Haz clic en el icono del candado en la barra de direcciones</li>
            <li>Busca "Notificaciones" en los permisos</li>
            <li>Cambia a "Permitir"</li>
            <li>Recarga la página</li>
          </ol>
        </div>
      )}

      {/* WhatsApp Notifications */}
      <div className="border-t border-slate-700 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${whatsappStatus?.configured ? 'bg-green-500/20' : 'bg-slate-700'}`}>
              <MessageCircle className={`w-5 h-5 ${whatsappStatus?.configured ? 'text-green-400' : 'text-slate-400'}`} />
            </div>
            <div>
              <h4 className="text-white font-medium">Notificaciones por WhatsApp</h4>
              <p className="text-slate-400 text-sm">
                {whatsappStatus?.configured 
                  ? 'Recibirás mensajes de WhatsApp cuando lleguen leads'
                  : 'Configura WhatsApp Cloud API para recibir alertas'}
              </p>
            </div>
          </div>

          {whatsappStatus?.configured ? (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Configurado
              </span>
              <button
                onClick={handleTestWhatsapp}
                disabled={testingWhatsapp}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors text-sm"
              >
                {testingWhatsapp ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Probar
              </button>
            </div>
          ) : (
            <a
              href="/memory/WHATSAPP_SETUP_GUIDE.md"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Ver guía
            </a>
          )}
        </div>

        {!whatsappStatus?.configured && (
          <div className="mt-4 bg-slate-800/50 rounded-lg p-4">
            <h5 className="text-white text-sm font-medium mb-2">Para configurar WhatsApp:</h5>
            <ol className="text-slate-400 text-sm space-y-1 list-decimal list-inside">
              <li>Accede a tu App en Meta Developer Portal</li>
              <li>Agrega el producto "WhatsApp" a tu app</li>
              <li>Obtén el Phone Number ID y Access Token</li>
              <li>Contáctanos para agregar las credenciales</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

